import React from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { 
  ArrowLeft, Check, CheckCircle, 
  Shield, Clock, Crown, Star,
  MessageSquare, Heart, Zap, BadgeCheck, Home, Gift, Sparkles, Users, Search
} from 'lucide-react';

const PremiumPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { language } = useLanguage();

  const isVerhuurder = user?.userType === 'verhuurder';

  const freeFeatures = [
    { icon: Heart, text: language === 'nl' ? 'Onbeperkt swipen' : 'Unlimited swiping', desc: language === 'nl' ? 'Swipe zoveel als je wilt!' : 'Swipe as much as you want!' },
    { icon: MessageSquare, text: language === 'nl' ? 'Direct berichten sturen' : 'Direct messaging', desc: language === 'nl' ? 'Chat direct met verhuurders' : 'Chat directly with landlords' },
    { icon: Search, text: language === 'nl' ? 'Alle zoekfilters' : 'All search filters', desc: language === 'nl' ? 'Vind precies wat je zoekt' : 'Find exactly what you want' },
    { icon: BadgeCheck, text: language === 'nl' ? 'Geverifieerde verhuurders' : 'Verified landlords', desc: language === 'nl' ? 'Betrouwbare advertenties' : 'Trustworthy listings' },
    { icon: Zap, text: language === 'nl' ? 'Directe matches' : 'Instant matches', desc: language === 'nl' ? 'Weet meteen wanneer er interesse is' : 'Know instantly when there\'s interest' },
    { icon: Shield, text: language === 'nl' ? '100% veilig & gratis' : '100% safe & free', desc: language === 'nl' ? 'Geen verborgen kosten' : 'No hidden costs' }
  ];

  const testimonials = [
    { name: 'Sophie de Vries', city: 'Amsterdam', text: language === 'nl' ? 'Binnen 5 dagen een studio gevonden in De Pijp! Helemaal gratis!' : 'Found a studio in De Pijp within 5 days! Completely free!', rating: 5, initial: 'S', color: 'bg-pink-500' },
    { name: 'Daan Bakker', city: 'Rotterdam', text: language === 'nl' ? 'Eindelijk een platform waar je gratis kunt zoeken én direct kunt chatten!' : 'Finally a platform where you can search for free and chat directly!', rating: 5, initial: 'D', color: 'bg-blue-500' },
    { name: 'Lisa Jansen', city: 'Utrecht', text: language === 'nl' ? 'Als internationale student is dit perfect - alles gratis en supersimpel!' : 'Perfect for international students - everything free and super simple!', rating: 5, initial: 'L', color: 'bg-purple-500' },
    { name: 'Mark van den Berg', city: 'Den Haag', text: language === 'nl' ? 'Ik kon niet geloven dat dit gratis is. Binnen een week 3 bezichtigingen!' : 'Couldn\'t believe this is free. 3 viewings within a week!', rating: 5, initial: 'M', color: 'bg-green-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D2137] via-[#1F3A5F] to-[#0D2137]" data-testid="premium-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">{language === 'nl' ? 'Terug' : 'Back'}</span>
          </button>
          <SwipeMatchLogo size="small" variant="white" />
          <button 
            onClick={() => navigate('/')} 
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <Home className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="pt-20 pb-12">
        {/* Hero Section - FREE Banner */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          {/* Big FREE Badge */}
          <div className="inline-flex items-center justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-[#2E7D32] blur-3xl opacity-30 scale-150"></div>
              <div className="relative bg-gradient-to-r from-[#2E7D32] to-[#4CAF50] text-white px-8 py-4 rounded-full text-2xl sm:text-3xl font-black flex items-center gap-3 shadow-2xl">
                <Gift className="w-8 h-8 sm:w-10 sm:h-10" />
                <span>100% GRATIS</span>
                <Sparkles className="w-8 h-8 sm:w-10 sm:h-10" />
              </div>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
            {language === 'nl' 
              ? 'Alle functies zijn nu gratis!' 
              : 'All features are now free!'}
          </h1>
          
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-8">
            {language === 'nl'
              ? 'Wij geloven dat iedereen toegang moet hebben tot een goede woning. Daarom is Swipe je woning nu helemaal gratis te gebruiken - geen verborgen kosten, geen abonnementen!'
              : 'We believe everyone deserves access to good housing. That\'s why Swipe je woning is now completely free to use - no hidden costs, no subscriptions!'}
          </p>

          {/* CTA Button */}
          <Button
            size="lg"
            onClick={() => navigate(isAuthenticated ? '/swipematch/swipe' : '/registreren')}
            className="bg-[#2E7D32] hover:bg-[#256B2B] text-white px-10 py-6 rounded-xl text-lg font-bold shadow-lg shadow-[#2E7D32]/30"
          >
            <Heart className="w-6 h-6 mr-2" />
            {isAuthenticated 
              ? (language === 'nl' ? 'Start met swipen' : 'Start swiping')
              : (language === 'nl' ? 'Gratis aanmelden' : 'Sign up free')}
          </Button>

          <p className="mt-4 text-white/60 text-sm">
            {language === 'nl' ? 'Geen creditcard nodig • Direct beginnen' : 'No credit card needed • Start immediately'}
          </p>
        </section>

        {/* Features Grid */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">
            {language === 'nl' ? 'Alles wat je nodig hebt, helemaal gratis' : 'Everything you need, completely free'}
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {freeFeatures.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#2E7D32] rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  {feature.text}
                  <span className="text-xs bg-[#2E7D32] text-white px-2 py-0.5 rounded-full">GRATIS</span>
                </h3>
                <p className="text-white/70 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Free Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <div className="bg-gradient-to-r from-[#2E7D32]/20 to-[#4CAF50]/20 border border-[#2E7D32]/30 rounded-3xl p-8 sm:p-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#2E7D32] rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {language === 'nl' ? 'Waarom gratis?' : 'Why free?'}
                </h3>
                <p className="text-white/60">{language === 'nl' ? 'Onze missie' : 'Our mission'}</p>
              </div>
            </div>
            <p className="text-white/80 text-lg leading-relaxed">
              {language === 'nl'
                ? 'De woningmarkt is al moeilijk genoeg. Wij willen het zoeken naar een woning toegankelijk maken voor iedereen - studenten, starters, en gezinnen. Door onze service gratis aan te bieden, kunnen wij meer mensen helpen hun droomwoning te vinden. Geen betaalmuren, geen beperkingen - gewoon swipen en matchen!'
                : 'The housing market is tough enough. We want to make finding a home accessible for everyone - students, starters, and families. By offering our service for free, we can help more people find their dream home. No paywalls, no limits - just swipe and match!'}
            </p>
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">
            {language === 'nl' ? 'Wat anderen zeggen' : 'What others say'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-xl"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 ${testimonial.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {testimonial.initial}
                  </div>
                  <div>
                    <p className="font-semibold text-[#2A2A2A]">{testimonial.name}</p>
                    <p className="text-sm text-[#9E9E9E]">{testimonial.city}</p>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                    ))}
                  </div>
                </div>
                <p className="text-[#2A2A2A]">{testimonial.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-[#2E7D32] to-[#4CAF50] rounded-3xl p-8 sm:p-12 shadow-2xl">
            <CheckCircle className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {language === 'nl' ? 'Klaar om te beginnen?' : 'Ready to start?'}
            </h2>
            <p className="text-white/90 text-lg mb-8">
              {language === 'nl'
                ? 'Meld je gratis aan en begin vandaag nog met het vinden van je droomwoning!'
                : 'Sign up for free and start finding your dream home today!'}
            </p>
            <Button
              size="lg"
              onClick={() => navigate(isAuthenticated ? '/swipematch/swipe' : '/registreren')}
              className="bg-white text-[#2E7D32] hover:bg-white/90 px-10 py-6 rounded-xl text-lg font-bold"
            >
              {isAuthenticated 
                ? (language === 'nl' ? 'Start met swipen' : 'Start swiping')
                : (language === 'nl' ? 'Gratis aanmelden' : 'Sign up free')}
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PremiumPage;
